/**
 * Admin -> Business unit source (CSV import)
 *
 * Bulk, non-core path for loading a business-unit tree, for tenants that do not
 * run the paired privacy core.
 *
 * Deliberate flow: pick a file, PREVIEW, then apply. The preview runs the same
 * read and validation the real import runs and only skips the writes, so what
 * you see is what lands. Business units scope access and data, so the import
 * never deletes - units missing from the file are reported as drift for a human
 * to act on.
 *
 * Backend: GET / PUT /v1/admin/bu-source, POST /v1/admin/bu-source/import-csv.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import {
  AlertTriangle,
  CheckCircle2,
  FileUp,
  Loader2,
  RefreshCw,
  Save,
  Upload,
} from 'lucide-react';

import { GlassCard, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { PageContainer, PageHeader } from '@/components/viz/PageHeader';
import { ErrorState, LoadingState } from '@foctta/ui-shell';
import {
  getBuSource,
  setBuSource,
  importBuCsv,
  type BuSourceState,
  type BuImportResult,
} from '@/lib/api';

const SAMPLE = `external_ref,name,slug,parent_ref,description
BU-100,Retail Banking,retail-banking,,Consumer division
BU-110,Home Loans,home-loans,BU-100,Mortgages`;

const MAX_BYTES = 2 * 1024 * 1024;

export function AdminBuSource(): JSX.Element {
  const [state, setState] = useState<BuSourceState | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [kind, setKind] = useState('manual');
  const [enabled, setEnabled] = useState(false);
  const [savingSource, setSavingSource] = useState(false);

  const [csv, setCsv] = useState('');
  const [fileName, setFileName] = useState<string | null>(null);
  const [busy, setBusy] = useState<'preview' | 'apply' | null>(null);
  const [preview, setPreview] = useState<BuImportResult | null>(null);
  const [applied, setApplied] = useState<BuImportResult | null>(null);
  const fileRef = useRef<HTMLInputElement | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const s = await getBuSource();
      setState(s);
      if (s.source) {
        setKind(s.source.kind);
        setEnabled(s.source.enabled);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load business-unit source');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  async function onPickFile(e: React.ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > MAX_BYTES) {
      setError(`File is ${(f.size / 1024 / 1024).toFixed(1)} MB - the limit is 2 MB.`);
      return;
    }
    setError(null);
    setCsv(await f.text());
    setFileName(f.name);
    setPreview(null);
    setApplied(null);
  }

  async function run(dryRun: boolean) {
    setBusy(dryRun ? 'preview' : 'apply');
    setError(null);
    try {
      const res = await importBuCsv(csv, dryRun);
      if (dryRun) {
        setPreview(res);
        setApplied(null);
      } else {
        setApplied(res);
        setPreview(null);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Import failed');
    } finally {
      setBusy(null);
    }
  }

  async function onSaveSource() {
    setSavingSource(true);
    try {
      await setBuSource({ kind, enabled });
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Save failed');
    } finally {
      setSavingSource(false);
    }
  }

  const result = applied ?? preview;

  return (
    <PageContainer>
      <PageHeader
        title="Business unit source"
        subtitle="Choose how business units are maintained, and bulk load them from a file."
        badges={[{ label: `Effective: ${state?.effective ?? 'manual'}`, variant: 'glass' }]}
        actions={
          <Button size="sm" variant="glass" onClick={() => void load()}>
            <RefreshCw className="h-4 w-4" /> Refresh
          </Button>
        }
      />

      {loading && <LoadingState caption="Loading business-unit source..." />}
      {error && !loading && <ErrorState title="Business unit source" detail={error} onRetry={() => void load()} />}

      {!loading && state && (
        <>
          {/* Source selection */}
          <GlassCard className="p-5 mb-4 space-y-3">
            <CardHeader className="p-0">
              <CardTitle className="text-base">Where business units come from</CardTitle>
              <CardDescription className="text-xs">
                {state.note ?? 'Manual authoring always remains available.'}
              </CardDescription>
            </CardHeader>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
              {state.catalogue.map((c) => (
                <button
                  key={c.kind}
                  type="button"
                  onClick={() => setKind(c.kind)}
                  className={`text-left glass-lite rounded-md p-3 border transition ${
                    kind === c.kind ? 'border-primary/60' : 'border-border/40'
                  }`}
                >
                  <div className="text-sm font-medium flex items-center gap-2">
                    {c.label}
                    {c.requiresPairing && (
                      <Badge variant="glass" className="text-[9px]">
                        Needs pairing
                      </Badge>
                    )}
                  </div>
                  <div className="text-[11px] text-muted-foreground mt-1">{c.description}</div>
                </button>
              ))}
            </div>
            <div className="flex items-center gap-3">
              <label className="flex items-center gap-2 text-xs">
                <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />
                Enabled
              </label>
              <Button size="sm" onClick={() => void onSaveSource()} disabled={savingSource}>
                {savingSource ? <Loader2 className="h-3 w-3 animate-spin" /> : <Save className="h-3 w-3" />} Save
              </Button>
            </div>
          </GlassCard>

          {/* CSV import */}
          <GlassCard className="p-5 space-y-4">
            <CardHeader className="p-0">
              <CardTitle className="text-base flex items-center gap-2">
                <FileUp className="h-4 w-4 text-primary" /> Import from CSV
              </CardTitle>
              <CardDescription className="text-xs">
                Columns: <code>external_ref</code>, <code>name</code>, and optionally{' '}
                <code>slug</code>, <code>parent_ref</code>, <code>description</code>. Re-importing the
                same file updates in place. Units missing from the file are reported, never deleted.
              </CardDescription>
            </CardHeader>

            <div className="flex flex-wrap items-center gap-2">
              <input
                ref={fileRef}
                type="file"
                accept=".csv,.txt"
                className="hidden"
                onChange={(e) => void onPickFile(e)}
              />
              <Button size="sm" variant="glass" onClick={() => fileRef.current?.click()}>
                <Upload className="h-3 w-3" /> Choose file
              </Button>
              {fileName && <span className="text-[11px] text-muted-foreground">{fileName}</span>}
              <Button size="sm" variant="glass" onClick={() => setCsv(SAMPLE)}>
                Use sample
              </Button>
            </div>

            <textarea
              value={csv}
              onChange={(e) => {
                setCsv(e.target.value);
                setPreview(null);
                setApplied(null);
              }}
              rows={8}
              placeholder={SAMPLE}
              className="glass-lite rounded-md px-3 py-2 text-xs w-full outline-none focus:border-primary/40 border border-border/40 font-mono resize-y"
            />

            <div className="flex flex-wrap items-center gap-2">
              <Button size="sm" variant="glass" onClick={() => void run(true)} disabled={!csv.trim() || busy !== null}>
                {busy === 'preview' ? <Loader2 className="h-3 w-3 animate-spin" /> : null} Preview
              </Button>
              <Button
                size="sm"
                onClick={() => void run(false)}
                disabled={!csv.trim() || busy !== null || preview === null}
                title={preview === null ? 'Preview first' : 'Apply the import'}
              >
                {busy === 'apply' ? <Loader2 className="h-3 w-3 animate-spin" /> : null} Apply import
              </Button>
              {preview && !applied && (
                <span className="text-[11px] text-muted-foreground">
                  Preview only - nothing has been written yet.
                </span>
              )}
              {applied && (
                <span className="text-[11px] text-success flex items-center gap-1">
                  <CheckCircle2 className="h-3 w-3" /> Import applied
                </span>
              )}
            </div>

            {result && (
              <div className="space-y-3">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                  {[
                    { l: 'Created', v: result.created },
                    { l: 'Updated', v: result.updated },
                    { l: 'Parents linked', v: result.parentsLinked },
                    { l: 'Rejected', v: result.rejected },
                  ].map((s) => (
                    <div key={s.l} className="glass-lite rounded p-2 text-center">
                      <div className="text-lg font-semibold tabular">{s.v}</div>
                      <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{s.l}</div>
                    </div>
                  ))}
                </div>

                {result.rejections.length > 0 && (
                  <div className="glass-lite rounded-md p-3 border border-amber-500/40">
                    <div className="text-xs font-medium flex items-center gap-2 mb-1">
                      <AlertTriangle className="h-3.5 w-3.5 text-amber-400" /> Rows needing attention
                    </div>
                    <ul className="text-[11px] space-y-1">
                      {result.rejections.map((r, i) => (
                        <li key={i}>
                          <span className="tabular text-muted-foreground">line {r.line}:</span> {r.reason}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {result.drift.length > 0 && (
                  <div className="glass-lite rounded-md p-3 border border-border/40">
                    <div className="text-xs font-medium mb-1">
                      Previously imported, not in this file ({result.drift.length})
                    </div>
                    <div className="text-[11px] text-muted-foreground mb-2">
                      These were left untouched. Archive them manually if they are genuinely gone -
                      removing a business unit also removes what it scopes.
                    </div>
                    <ul className="text-[11px] space-y-0.5">
                      {result.drift.map((d) => (
                        <li key={d.externalRef}>
                          <code>{d.externalRef}</code> - {d.name}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </GlassCard>
        </>
      )}
    </PageContainer>
  );
}
